/**
 * Browser-safe copy of the canonical Mailchimp tag helpers.
 * Keep this in sync with functions/shared/mailchimpTags.js; CRA cannot import
 * shared modules outside src/.
 */
export const FUTURE_BEHAVIORAL_TAGS = [
  "trial-expiring-soon",
  "activation-unactivated",
  "activation-partial",
  "activation-activated",
  "power-user",
  "churn-risk-high",
  "high-engagement",
  "low-engagement",
];

export const ALL_SUPPORTED_MAILCHIMP_TAGS = new Set([
  "active-dates-needs-help",
  "trial-active",
  "trial-expired",
  "paid-monthly",
  "paid-annual",
  "paused",
  "churned",
  "school-pilot",
  "school-site",
  "school-multi-seat",
  "school-license-expired",
  "referred-signup",
  "referral-converted",
  "bts-preview-active",
  "bts-preview-used",
  "fall-winback-preview-activated",
]);

const BACK_TO_SCHOOL_PREVIEW_CAMPAIGN_ID = "back-to-school-2026";
const FALL_WINBACK_CAMPAIGN_ID = "fall-winback-2026";

const SCHOOL_EXPIRED_INTERNAL_TAGS = new Set([
  "School Pilot Expired",
  "School Site Expired",
  "School Multi-Seat Expired",
]);

export function isDemoUser(user = {}) {
  const tags = Array.isArray(user.tags) ? user.tags : [];
  return tags.includes("Demo");
}

export function isSchoolLicensedUser(user = {}) {
  const licenseType = String(user.schoolLicenseType || "").toLowerCase();
  if (["site", "multi-seat"].includes(licenseType)) {
    return true;
  }

  const tags = Array.isArray(user.tags) ? user.tags : [];
  return tags.some((tag) => ["School Site License", "School Multi-Seat License"].includes(tag));
}

function isSchoolPilotUser(user = {}) {
  const licenseType = String(user.schoolLicenseType || "").toLowerCase();
  if (licenseType === "pilot") {
    return true;
  }

  const tags = Array.isArray(user.tags) ? user.tags : [];
  return tags.includes("School Pilot");
}

function toDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "object" && typeof value.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value === "number") return new Date(value > 1e12 ? value : value * 1000);
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return new Date(parsed);
  }
  return null;
}

function endOfUtcDay(date) {
  const end = new Date(date.getTime());
  end.setUTCHours(23, 59, 59, 999);
  return end;
}

export function hasExpiredSchoolLicense(user = {}) {
  const tags = Array.isArray(user.tags) ? user.tags : [];
  if (tags.some((tag) => SCHOOL_EXPIRED_INTERNAL_TAGS.has(tag))) {
    return true;
  }

  const licenseType = String(user.schoolLicenseType || "").toLowerCase();
  if (!["pilot", "site", "multi-seat"].includes(licenseType)) {
    return false;
  }

  const endDate = toDateValue(user.newTrialEndDate);
  return Boolean(endDate && endOfUtcDay(endDate).getTime() < Date.now());
}

function getBillingPeriod(user = {}) {
  const billingPeriod = String(user.personalSubscriptionBilling?.billingPeriod || "").toLowerCase();
  if (billingPeriod === "annual" || billingPeriod === "yearly") {
    return "annual";
  }
  if (billingPeriod === "monthly") {
    return "monthly";
  }
  return null;
}

export function isPaused(user = {}) {
  return user.subscriptionPause?.active === true;
}

export function hasPaidHistory(user = {}) {
  return Boolean(
    user.personalPremiumStartedAt ||
      user.personalPremiumEndedAt ||
      user.personalSubscriptionBilling?.stripeSubscriptionId ||
      user.personalSubscriptionChurn?.recordedAt ||
      user.personalSubscriptionChurn?.stripeStatusAtChurn ||
      user.stripeCustomerId
  );
}

function hasTrialHistory(user = {}) {
  const internalTags = Array.isArray(user.tags) ? user.tags : [];
  return Boolean(
    user.accountCreated ||
      user.newTrialEndDate ||
      internalTags.includes("Trial") ||
      String(user.schoolLicenseType || "").toLowerCase() === "pilot"
  );
}

export function hasActiveSchoolLicense(user = {}) {
  return (isSchoolLicensedUser(user) || isSchoolPilotUser(user)) && !hasExpiredSchoolLicense(user);
}

export function isChurned(user = {}) {
  if (user.premium === true) {
    return false;
  }
  if (isPaused(user)) {
    return false;
  }
  if (hasActiveSchoolLicense(user)) {
    return false;
  }

  return hasPaidHistory(user);
}

export function isTrialExpiredNoAccess(user = {}) {
  return (
    user.trial !== true &&
    user.premium !== true &&
    !isPaused(user) &&
    !hasActiveSchoolLicense(user) &&
    !hasExpiredSchoolLicense(user) &&
    !hasPaidHistory(user) &&
    hasTrialHistory(user)
  );
}

function hasClaimedBackToSchoolPreview(user = {}) {
  return (
    Boolean(user.backToSchoolPreviewClaimedAt) &&
    (!user.backToSchoolPreviewCampaign ||
      user.backToSchoolPreviewCampaign === BACK_TO_SCHOOL_PREVIEW_CAMPAIGN_ID)
  );
}

/**
 * Preview entitlement currently grants access (absolute timestamp, not calendar day).
 * Paid / paused / active school users keep their primary lifecycle tags and get
 * bts-preview-used instead so nurture automations stop once they leave the preview.
 */
function isBackToSchoolPreviewActiveForMailchimp(user = {}, now = Date.now()) {
  if (!hasClaimedBackToSchoolPreview(user)) {
    return false;
  }
  if (user.premium === true || isPaused(user) || hasActiveSchoolLicense(user)) {
    return false;
  }

  const expiresAt = toDateValue(user.backToSchoolPreviewExpiresAt);
  return Boolean(expiresAt && expiresAt.getTime() > now);
}

function appendBackToSchoolPreviewMailchimpTags(user = {}, tags = [], now = Date.now()) {
  if (!hasClaimedBackToSchoolPreview(user)) {
    return tags;
  }

  tags.push(
    isBackToSchoolPreviewActiveForMailchimp(user, now)
      ? "bts-preview-active"
      : "bts-preview-used"
  );
  return tags;
}

function getCampaignEntitlement(user = {}, campaignId = FALL_WINBACK_CAMPAIGN_ID) {
  const entitlements = user.campaignEntitlements || {};
  const entitlement = entitlements[campaignId];
  return entitlement && typeof entitlement === "object" ? entitlement : null;
}

function hasClaimedFallWinbackPreview(user = {}) {
  return Boolean(getCampaignEntitlement(user, FALL_WINBACK_CAMPAIGN_ID)?.claimedAt);
}

function isFallWinbackPreviewActiveForMailchimp(user = {}, now = Date.now()) {
  const entitlement = getCampaignEntitlement(user, FALL_WINBACK_CAMPAIGN_ID);
  if (!entitlement?.claimedAt) {
    return false;
  }
  if (user.premium === true || isPaused(user) || hasActiveSchoolLicense(user)) {
    return false;
  }

  const expiresAt = toDateValue(entitlement.expiresAt);
  return Boolean(expiresAt && expiresAt.getTime() > now);
}

function appendFallWinbackMailchimpTags(user = {}, tags = []) {
  if (hasClaimedFallWinbackPreview(user)) {
    tags.push("fall-winback-preview-activated");
  }
  return tags;
}

function finalizeMailchimpTags(user = {}, tags = [], now = Date.now()) {
  return normalizeMailchimpTags(
    appendFallWinbackMailchimpTags(
      user,
      appendBackToSchoolPreviewMailchimpTags(user, tags, now)
    )
  );
}

function normalizeMailchimpTags(tags = []) {
  const uniqueTags = [...new Set(tags)].filter((tag) => ALL_SUPPORTED_MAILCHIMP_TAGS.has(tag));
  uniqueTags.sort();
  return uniqueTags;
}

export function buildMailchimpTags(user = {}, _analytics = {}, options = {}) {
  if (isDemoUser(user)) {
    return [];
  }

  const now =
    options.now != null
      ? options.now instanceof Date
        ? options.now.getTime()
        : Number(options.now)
      : Date.now();
  const tags = [];

  if (user.referredByCode || user.referredByUid) {
    tags.push("referred-signup");
  }

  if (user.referralConversionRewarded === true || user.referralFirstPaidAt) {
    tags.push("referral-converted");
  }

  if (user.activeDatesEducation?.emptyPresentUnderstandingLevel === "confused") {
    tags.push("active-dates-needs-help");
  }

  if (isPaused(user)) {
    tags.push("paused");
    return finalizeMailchimpTags(user, tags, now);
  }

  if (hasExpiredSchoolLicense(user)) {
    tags.push("school-license-expired");
    return finalizeMailchimpTags(user, tags, now);
  }

  if (isSchoolPilotUser(user)) {
    tags.push("school-pilot");
    return finalizeMailchimpTags(user, tags, now);
  }

  if (
    String(user.schoolLicenseType || "").toLowerCase() === "site" ||
    (Array.isArray(user.tags) && user.tags.includes("School Site License"))
  ) {
    tags.push("school-site");
    return finalizeMailchimpTags(user, tags, now);
  }

  if (
    String(user.schoolLicenseType || "").toLowerCase() === "multi-seat" ||
    (Array.isArray(user.tags) && user.tags.includes("School Multi-Seat License"))
  ) {
    tags.push("school-multi-seat");
    return finalizeMailchimpTags(user, tags, now);
  }

  const billingPeriod = getBillingPeriod(user);
  if (user.premium === true) {
    if (billingPeriod === "annual") {
      tags.push("paid-annual");
    } else if (billingPeriod === "monthly") {
      tags.push("paid-monthly");
    }
    return finalizeMailchimpTags(user, tags, now);
  }

  if (isChurned(user)) {
    tags.push("churned");
    return finalizeMailchimpTags(user, tags, now);
  }

  if (user.trial === true) {
    tags.push("trial-active");
    return finalizeMailchimpTags(user, tags, now);
  }

  // Active preview users still have access, so do not keep them on trial-expired nurture.
  if (
    isTrialExpiredNoAccess(user) &&
    !isBackToSchoolPreviewActiveForMailchimp(user, now) &&
    !isFallWinbackPreviewActiveForMailchimp(user, now)
  ) {
    tags.push("trial-expired");
  }

  return finalizeMailchimpTags(user, tags, now);
}

export function tagsEqual(left = [], right = []) {
  const normalizedLeft = [...new Set(Array.isArray(left) ? left : [])].sort();
  const normalizedRight = [...new Set(Array.isArray(right) ? right : [])].sort();

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((tag, index) => tag === normalizedRight[index]);
}

export function buildMailchimpMergeFieldPayload(user = {}, analytics = {}) {
  const billingPeriod = getBillingPeriod(user) || "";
  let subStatus = "no-access";

  if (isDemoUser(user)) {
    subStatus = "demo";
  } else if (isPaused(user)) {
    subStatus = "paused";
  } else if (isChurned(user)) {
    subStatus = "churned";
  } else if (user.premium === true && isSchoolLicensedUser(user)) {
    subStatus = "school-licensed";
  } else if (user.premium === true) {
    subStatus = billingPeriod === "annual" ? "paid-annual" : "paid-monthly";
  } else if (user.trial === true) {
    subStatus = "trial";
  }

  return {
    TRIAL_END: user.newTrialEndDate || analytics.trialEndsAt || null,
    TRIAL_DAYS: analytics.trialDaysRemaining ?? null,
    BILLING_PERIOD: billingPeriod,
    SUB_STATUS: subStatus,
    ACTIVATION_SCORE: analytics.activationScore ?? null,
    HEALTH_SCORE: analytics.healthScore ?? null,
    CHURN_RISK: analytics.churnRisk || null,
    SCHOOL_NAME: user.schoolName || user.school?.name || null,
    LEAD_SOURCE: user.leadData?.leadSource || null,
    REFERRED_BY: user.referredByCode || null,
  };
}
